import { Cache } from '@urql/exchange-graphcache';

import {
  GQGetProductFootprintsMetadataQuery,
  GQGetProductFootprintUsageAndLimitsQuery,
} from '../../generated/graphql-operations';
import {
  GetProductFootprintsMetadataDocument,
  GetProductFootprintUsageAndLimitsDocument,
} from '../../generated/urql';
import { invalidateAllTopLevelQueriesForNode } from '../cache';

/**
 * Optimistically increment the running count in the metadata cache
 * so the run progress banner updates immediately, even before the
 * server reflects the newly created runs.
 */
export function incrementRunProgressInCache(
  cache: Cache,
  additionalRunningCount: number
): void {
  cache.updateQuery(
    { query: GetProductFootprintsMetadataDocument },
    (data: GQGetProductFootprintsMetadataQuery | null) => {
      if (!data) {
        return null;
      }
      return {
        ...data,
        materialVariantsRunProgress: {
          ...data.materialVariantsRunProgress,
          runningCount:
            data.materialVariantsRunProgress.runningCount +
            additionalRunningCount,
          // Clear estimated time — polling will fetch the real value from the server
          estimatedTimeRemainingMinutes: null,
        },
      };
    }
  );
}

/**
 * Optimistically increment in-progress runs in the usage and limits cache
 * so the UI reflects the new runs before the server response arrives.
 */
export function incrementUsageAndLimitsInCache(
  cache: Cache,
  additionalRunningCount: number,
  runType: 'flash' | 'proxyMapping'
): void {
  cache.updateQuery(
    { query: GetProductFootprintUsageAndLimitsDocument },
    (data: GQGetProductFootprintUsageAndLimitsQuery | null) => {
      if (!data) {
        return null;
      }
      return {
        ...data,
        productFootprintUsageAndLimits: {
          ...data.productFootprintUsageAndLimits,
          [runType]: {
            ...data.productFootprintUsageAndLimits[runType],
            inProgressRuns:
              data.productFootprintUsageAndLimits[runType].inProgressRuns +
              additionalRunningCount,
          },
        },
      };
    }
  );
}

/**
 * Invalidate material variant graphql nodes by lineage id.
 * This includes MaterialVariant and MaterialVariantWithPcf nodes.
 */
export function invalidateMaterialVariantNodesByLineageId(
  cache: Cache,
  targetLineageId: string
) {
  // Invalidate the relevant MaterialVariant cache entry.
  // MaterialVariant nodes are cached by lineage id, so the lineage id is the
  // normalized entity id used by graphcache.
  cache.invalidate({
    __typename: 'MaterialVariant',
    lineageId: targetLineageId,
  });

  // Invalidate the relevant MaterialVariantWithPcf cache entries
  // Since these rows are paginated, there is not as big a performance hit as
  // invalidating all material variants.
  invalidateAllTopLevelQueriesForNode(cache, 'materialVariantsWithPcfs');

  invalidateAllTopLevelQueriesForNode(cache, 'pcfSelectionStats');
}
